import React, { useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { AiModelPicker, DEFAULT_PICK } from "./AiModelPicker";
import { Sparkles, Repeat, Trash2, Wrench, Store, Lightbulb } from "lucide-react";

const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

function List({ icon: Icon, title, items, color, field, testid }) {
  return (
    <div className="rounded-xl border border-zinc-800 p-3" data-testid={testid}>
      <div className={`text-xs font-bold flex items-center gap-1.5 mb-2 ${color}`}><Icon className="h-3.5 w-3.5" /> {title} <span className="font-mono text-zinc-500">{items.length}</span></div>
      {items.length === 0 && <div className="text-[11px] text-zinc-600">Nessun prodotto</div>}
      <ul className="space-y-1.5">{items.map((it, i) => <li key={i} className="text-[11px]"><span className="font-semibold">{it.prodotto}</span><span className="text-zinc-400"> — {it[field]}</span></li>)}</ul>
    </div>
  );
}

export function FlyerAiAdvice({ flyerId, advice, stores, fidelityOnly, weekdays, hours, onDone }) {
  const [pick, setPick] = useState(advice?.provider ? { provider: advice.provider, model: advice.model } : DEFAULT_PICK);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/promo-flyers/${flyerId}/ai-advice`, { ...pick, stores: stores.length ? stores.join("|") : undefined, fidelity_only: fidelityOnly, weekdays, hours });
      toast.success("Analisi AI completata"); onDone?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-elev p-4 space-y-3" data-testid="flyer-ai">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[color:var(--paprika)]" /> Consigli AI sul volantino</div>
          <p className="text-[11px] text-zinc-500 mt-0.5">L'AI legge uplift, penetrazione, prezzi effettivi, sconti e sedi e suggerisce quali promozioni ripetere, togliere o correggere.{advice?.created_at ? ` Ultima analisi ${fmtAt(advice.created_at)} (${advice.model}${advice.stores?.length ? `, ${advice.stores.length} sedi` : ""}).` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <AiModelPicker value={pick} onChange={setPick} testid="flyer-ai-pick" />
          <Button size="sm" disabled={busy} onClick={run} data-testid="flyer-ai-run" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"><Sparkles className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-pulse" : ""}`} /> {busy ? "Analisi in corso…" : advice ? "Rianalizza" : "Chiedi all'AI"}</Button>
        </div>
      </div>
      {advice && (
        <div className="space-y-3" data-testid="flyer-ai-result">
          {advice.sintesi && <p className="text-sm text-zinc-200" data-testid="flyer-ai-summary">{advice.sintesi}</p>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <List icon={Repeat} title="Da ripetere" items={advice.ripetere} color="text-emerald-400" field="motivo" testid="flyer-ai-repeat" />
            <List icon={Trash2} title="Da togliere" items={advice.togliere} color="text-red-400" field="motivo" testid="flyer-ai-remove" />
            <List icon={Wrench} title="Da correggere" items={advice.correggere} color="text-amber-300" field="azione" testid="flyer-ai-adjust" />
          </div>
          {advice.sedi && <p className="text-[11px] text-zinc-400 flex items-start gap-1.5" data-testid="flyer-ai-stores"><Store className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {advice.sedi}</p>}
          {advice.consigli?.length > 0 && (
            <div className="text-[11px] text-zinc-300" data-testid="flyer-ai-tips"><div className="font-bold flex items-center gap-1.5 mb-1"><Lightbulb className="h-3.5 w-3.5 text-yellow-400" /> Per il prossimo volantino</div>
              <ol className="list-decimal pl-5 space-y-0.5">{advice.consigli.map((c, i) => <li key={i}>{c}</li>)}</ol></div>
          )}
        </div>
      )}
    </div>
  );
}

export function PriceCell({ flyerId, index, field, value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  const saving = useRef(false);
  const save = async () => {
    if (saving.current) return;
    saving.current = true;
    try {
      const raw = String(v).trim();
      if (raw === "") { await api.patch(`/promo-flyers/${flyerId}/items/${index}`, { clear: [field] }); setEditing(false); onSaved?.(); return; }
      const n = parseFloat(raw.replace(",", "."));
      if (Number.isNaN(n) || n < 0) { toast.error("Prezzo non valido"); return; }
      await api.patch(`/promo-flyers/${flyerId}/items/${index}`, { [field]: n }); setEditing(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { saving.current = false; }
  };
  if (editing) return <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} data-testid={`flyer-price-input-${field}-${index}`} title="Vuoto = rimuovi prezzo" className="w-16 h-6 text-xs text-right bg-zinc-950 border border-zinc-700 rounded px-1 font-mono" />;
  return <button type="button" onClick={() => { setV(value ?? ""); setEditing(true); }} data-testid={`flyer-price-${field}-${index}`} title="Clicca per modificare" className="font-mono hover:underline decoration-dotted">{value == null ? <span className="text-zinc-600">+ prezzo</span> : `€ ${Number(value).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</button>;
}
